// SubmissionApi.tsx

import {
  useQuery,
  useMutation,
  UseQueryOptions,
  QueryKey,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Small helper to replace :param placeholders with actual values
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? `:${key}`)
  );
}

/** ===== Types based on Postman collection and typical API responses ===== **/

export interface SubmissionFileInfo {
  id?: string;
  name?: string;
  path?: string;
  size?: number;
  type?: string;
}

export interface SubmissionUser {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
}

// Represents the nested project object
export interface SubmissionProject {
  id: string;
  title: string;
  course?: {
    // Course is nested under project
    id: string;
    name: string; // Assuming course name is available
  };
}

export interface SubmissionCheckpoint {
  id: string;
  title: string;
  checkpointNumber: number;
}

export interface SubmissionEntity {
  id: string;
  status: string;
  submittedAt: string | null;
  attempts: number;
  submissionSummary?: string | null;
  user: SubmissionUser;
  project: SubmissionProject;
  checkpoint: SubmissionCheckpoint;
  grade: any; // Kept as 'any' for now, can be refined
  files?: SubmissionFileInfo[];
  metadata?: {
    // Metadata can contain files and notebooks
    files?: any[];
    notebooks?: any[];
  };
  group?: {
    id: string;
    name: string;
    code: string;
    members: {
      id: string;
      name: string;
      email: string;
    }[];
  };
}

export interface GetAllSubmissionsData {
  checkpointProgress: SubmissionEntity[];
  // Include other pagination fields if they exist at this level
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
}

// Update the main response type to use the new data structure
export type GetAllSubmissionsResponse = ApiResponse<GetAllSubmissionsData>;

export interface Paginated<T> {
  checkpointProgress: SubmissionEntity[];
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface ApiResponse<T> {
  isSuccess?: boolean;
  success?: boolean;
  message: string;
  data: T;
}

export interface GetAllSubmissionsParams {
  page?: number;
  limit?: number;
  status?: string; // e.g., "in_progress"
  sortBy?: string; // e.g., "submitted_at"
  sortOrder?: "asc" | "desc";
}

export type GetSubmissionByIdResponse = ApiResponse<SubmissionEntity>;

// export interface updateSubmissionBody {
//   projectId: string;
//   submissionSummary?: string;
//   files?: File[]; // multipart/form-data
// }

export interface updateSubmissionBody {
  projectId: string;
  checkpointId?: string;
  submissionSummary?: string;
  files?: File[];
  existingFileIds?: string[];
  allowUpdate?: boolean;
}

export type updateSubmissionResponse = ApiResponse<SubmissionEntity>;

export interface AutosaveSubmissionBody {
  projectId: string;
  notebookContent?: string | object; // stringified JSON or object
  submissionSummary?: string;
  timeSpent?: number;
  metadata?: Record<string, any>;
  user_id?: string;
}

export type AutosaveSubmissionResponse = ApiResponse<SubmissionEntity>;

export type SubmitFinalAssignmentResponse = ApiResponse<{
  submissionId: string;
  status: string;
}>;

export type DownloadSubmissionNotebookResponse = Blob; // GET returns notebook file

export interface ProjectSubmissionStatistics {
  projectId: string;
  totals?: Record<string, number>;
  byStatus?: Record<string, number>;
  lastUpdated?: string;
  [k: string]: any;
}

export type GetProjectSubmissionStatisticsResponse =
  ApiResponse<ProjectSubmissionStatistics>;

export interface GetAllSubmissionsParams {
  page?: number;
  limit?: number;
  status?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  projectId?: string;
  courseId?: string;
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
  isExport?: boolean;
}

export type GetRecentSubmissionsResponse = ApiResponse<{
  submission: {
    studentNotes: string;
    submittedAt: string;
    status: string;
    files: {
      key: string;
      url: string;
      fileName: string;
    }[];
    checkPointNumber: number;
    checkPointTitle: string;
    checkPointDescription: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
    project: {
      id: string;
      title: string;
    };
    course: {
      id: string;
      name: string;
      code: string;
    };
  }[];
}>;

/** ==================== API functions ==================== **/

export async function getAllSubmissions(
  params: GetAllSubmissionsParams
): Promise<GetAllSubmissionsResponse> {
  const url = getBITSEndApiMap("getAllSubmissions");
  if (!params.isExport) {
    const { data } = await api.get<GetAllSubmissionsResponse>(url, { params });
    return data;
  } else {
    const response = await api.get<GetAllSubmissionsData>(url, {
      params,
      responseType: "blob",
    });
    return {
      isSuccess: true,
      message: "Success",
      data: response.data,
    } as GetAllSubmissionsResponse;
  }
}

export async function getSubmissionById(
  submissionId: string
): Promise<GetSubmissionByIdResponse> {
  const url = buildUrl(getBITSEndApiMap("getSubmissionById"), { submissionId });
  const { data } = await api.get<GetSubmissionByIdResponse>(url);
  return data;
}

export interface UpdateSubmissionJsonBody {
  projectId: string;
  checkpointId: string;
  submissionSummary?: string;
  metadata: {
    language: string;
    notebookType: string;
    environment: string;
  };
  timeSpentMinutes: number;
  max_attempts: number;
  filenamesToSubmit: string[];
}

export async function updateSubmission(
  body: UpdateSubmissionJsonBody
): Promise<updateSubmissionResponse> {
  const url = getBITSEndApiMap("updateSubmission");
  const { data } = await api.post<updateSubmissionResponse>(url, body);
  return data;
}

export async function autosaveSubmission(
  body: AutosaveSubmissionBody
): Promise<AutosaveSubmissionResponse> {
  const url = getBITSEndApiMap("autosaveSubmission");
  const { data } = await api.post<AutosaveSubmissionResponse>(url, body, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function submitFinalAssignment(
  submissionId: string
): Promise<SubmitFinalAssignmentResponse> {
  const url = buildUrl(getBITSEndApiMap("submitFinalAssignment"), {
    submissionId,
  });
  const { data } = await api.post<SubmitFinalAssignmentResponse>(url);
  return data;
}

export async function downloadSubmissionNotebook(
  submissionId: string
): Promise<DownloadSubmissionNotebookResponse> {
  const url = buildUrl(getBITSEndApiMap("downloadSubmissionNotebook"), {
    submissionId,
  });
  const response = await api.get(url, {
    responseType: "blob",
  });
  return response.data as Blob;
}

export async function getProjectSubmissionStatistics(
  projectId: string
): Promise<GetProjectSubmissionStatisticsResponse> {
  const url = buildUrl(getBITSEndApiMap("getProjectSubmissionStatistics"), {
    projectId,
  });
  const { data } = await api.get<GetProjectSubmissionStatisticsResponse>(url);
  return data;
}

export async function getRecentSubmissions(): Promise<GetRecentSubmissionsResponse> {
  const url = getBITSEndApiMap("getRecentSubmissions" as any);
  const { data } = await api.get<GetRecentSubmissionsResponse>(url);
  return data;
}

/** ==================== React Query Hooks ==================== **/

// GET => useQuery

export function useAllSubmissionsQuery(
  params: GetAllSubmissionsParams = {},
  options: Omit<
    UseQueryOptions<GetAllSubmissionsResponse>,
    "queryKey" | "queryFn"
  > = {}
) {
  return useQuery({
    queryKey: ["submissions", "list", params] as QueryKey,
    queryFn: () => getAllSubmissions(params),
    staleTime: 30_000,
    ...options,
  });
}

export function useSubmissionByIdQuery(
  submissionId: string | undefined,
  options: Omit<
    UseQueryOptions<GetSubmissionByIdResponse>,
    "queryKey" | "queryFn"
  > = {}
) {
  return useQuery({
    queryKey: ["submissions", "detail", submissionId] as QueryKey,
    queryFn: () => getSubmissionById(submissionId as string),
    enabled: !!submissionId,
    staleTime: 30_000,
    ...options,
  });
}

// Others => useMutation

export function useupdateSubmissionMutation() {
  return useMutation({
    mutationFn: updateSubmission,
  });
}

export function useAutosaveSubmissionMutation() {
  return useMutation({
    mutationFn: autosaveSubmission,
  });
}

export function useSubmitFinalAssignmentMutation() {
  return useMutation({
    mutationFn: submitFinalAssignment,
  });
}

export function useDownloadSubmissionNotebookMutation() {
  // returns Blob; caller can create ObjectURL and download
  return useMutation({
    mutationFn: downloadSubmissionNotebook,
  });
}

export function useProjectSubmissionStatisticsQuery(
  projectId: string | undefined,
  options: Omit<
    UseQueryOptions<GetProjectSubmissionStatisticsResponse>,
    "queryKey" | "queryFn"
  > = {}
) {
  return useQuery({
    queryKey: ["submissions", "project", "statistics", projectId] as QueryKey,
    queryFn: () => getProjectSubmissionStatistics(projectId as string),
    enabled: !!projectId,
    staleTime: 60_000,
    ...options,
  });
}

export function useRecentSubmissionsQuery(
  options: Omit<
    UseQueryOptions<
      GetRecentSubmissionsResponse,
      Error,
      GetRecentSubmissionsResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  > = {}
) {
  return useQuery<GetRecentSubmissionsResponse>({
    queryKey: ["submissions", "recent"] as QueryKey,
    queryFn: getRecentSubmissions,
    staleTime: 30_000,
    ...options,
  });
}
